/**
 * MDI Tab Store — Zustand store for browser-like tab management
 *
 * Syncs with react-router: each tab stores a route path.
 * Navigation triggers tab creation/activation.
 */

import { create } from 'zustand';

export interface MdiTab {
  id: string;
  title: string;
  path: string;
  icon?: string;
  closable: boolean;
}

interface MdiState {
  tabs: MdiTab[];
  activeTabId: string | null;
  sessionScope: string | null;

  /** Open a new tab or activate existing one matching path */
  openTab: (tab: Omit<MdiTab, 'id' | 'closable'> & { closable?: boolean }) => string;
  /** Create or update a tab without making it active */
  ensureTab: (tab: Omit<MdiTab, 'id' | 'closable'> & { closable?: boolean }) => string;
  /** Close a tab and return the next tab to activate */
  closeTab: (tabId: string) => string | null;
  /** Close all closable tabs except the first (dashboard) and navigate to it */
  closeAllTabs: () => string | null;
  /** Close all tabs in a scope and keep/create the scope's top-level dashboard tab */
  closeTabsToDashboard: (params: {
    dashboardPath: string;
    dashboardTitle: string;
    dashboardIcon?: string;
    scope?: (tab: MdiTab) => boolean;
  }) => string;
  /** Switch to an existing tab */
  activateTab: (tabId: string) => void;
  /** Update a tab's title */
  updateTabTitle: (tabId: string, title: string) => void;
  /** Bind tab persistence to the currently authenticated user/tenant scope */
  syncSessionScope: (scope: { tenantId?: string | null; userId?: string | null }) => void;
  /** Clear current tabs and persisted state for the active scope */
  resetTabs: () => void;
}

let nextTabId = 1;
const LEGACY_SESSION_KEY = 'boox_mdi_tabs';
const SESSION_KEY_PREFIX = 'boox_mdi_tabs:';
const FRESH_SESSION_KEY_PREFIX = 'boox_mdi_fresh_scope:';
let currentSessionKey: string | null = null;

function isRetiredDynamicTab(tab: MdiTab): boolean {
  const pathname = tab.path.split('?')[0] ?? tab.path;
  const retiredPrefixes = ['/cfm', '/pwa/cfm', '/share/cfm', '/hm', '/pwa/hm', '/share/hm'];
  return retiredPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    || /\bCFM\b/i.test(tab.title)
    || /\bHM\b/i.test(tab.title)
    || /CFS Freight|HM Haulage|Shared Haulage Job/i.test(tab.title);
}

function migrateTabs(tabs: MdiTab[]): MdiTab[] {
  return tabs
    .filter((tab) => !isRetiredDynamicTab(tab))
    .map((tab) => {
      const [tabPathname, tabQuery = ''] = tab.path.split('?');
      const tabMode = new URLSearchParams(tabQuery).get('mode');
      if ((tabPathname === '/wp/inbound/new' || tabPathname === '/wm/inbound/new') && tabMode === 'RETURN') {
        return {
          ...tab,
          path: tabPathname.startsWith('/wm') ? '/wm/returns/form' : '/wp/returns/form',
          title: 'Material Return',
        };
      }
      const pathname = tab.path.split('?')[0] ?? tab.path;
      if (pathname === '/crm' || pathname.startsWith('/crm/')) {
        return {
          ...tab,
          path: tab.path.replace(/^\/crm\b/, '/rm'),
          title: tab.title.replace(/\bCRM\b/g, 'RM'),
        };
      }
      if (pathname === '/tms' || pathname.startsWith('/tms/')) {
        return {
          ...tab,
          path: tab.path.replace(/^\/tms\b/, '/tm'),
          title: tab.title.replace(/\bTransport V1\b/g, 'Transport').replace(/\bTMS\b/g, 'TM'),
        };
      }
      if (pathname === '/wms' || pathname.startsWith('/wms/')) {
        return {
          ...tab,
          path: tab.path.replace(/^\/wms\b/, '/wm'),
          title: tab.title.replace(/\bWMS\b/g, 'WM'),
        };
      }
      if (pathname === '/hms' || pathname.startsWith('/hms/')) {
        return {
          ...tab,
          path: tab.path.replace(/^\/hms\b/, '/ct'),
          title: tab.title
            .replace(/\bHMS\b/g, 'CT')
            .replace(/\bHaulage Management V1\b/g, 'Container Trucking'),
        };
      }
      if (pathname === '/fms' || pathname.startsWith('/fms/')) {
        return {
          ...tab,
          path: tab.path.replace(/^\/fms\b/, '/fm'),
          title: tab.title.replace(/\bFMS\b/g, 'FM'),
        };
      }
      if (pathname === '/scm' || pathname.startsWith('/scm/')) {
        return {
          ...tab,
          path: tab.path.replace(/^\/scm\b/, '/sc'),
          title: tab.title.replace(/\bSCM\b/g, 'SC'),
        };
      }
      if (tab.path === '/e/conversations') {
        return { ...tab, path: '/inbox/conversations', title: 'Inbox', icon: 'message-square' };
      }
      if (tab.path === '/e/email-messages') {
        return { ...tab, path: '/inbox/email-messages', title: 'Email', icon: 'mail' };
      }
      return tab;
    });
}

function computeNextTabId(tabs: MdiTab[]) {
  nextTabId = tabs.length > 0
    ? Math.max(...tabs.map((t) => parseInt(t.id.replace('tab-', ''), 10) || 0)) + 1
    : 1;
}

function buildSessionKey(scope: { tenantId?: string | null; userId?: string | null }): string | null {
  if (!scope.userId) return null;
  const tenantId = scope.tenantId?.trim() || 'default-tenant';
  const userId = scope.userId.trim();
  return `${SESSION_KEY_PREFIX}${tenantId}:${userId}`;
}

function buildFreshSessionKey(sessionKey: string): string {
  return `${FRESH_SESSION_KEY_PREFIX}${sessionKey}`;
}

export function markMdiScopeForFreshStart(scope: { tenantId?: string | null; userId?: string | null }): void {
  const sessionKey = buildSessionKey(scope);
  if (!sessionKey) return;
  try {
    sessionStorage.setItem(buildFreshSessionKey(sessionKey), '1');
  } catch { /* ignore */ }
}

function consumeFreshSessionMarker(sessionKey: string): boolean {
  const markerKey = buildFreshSessionKey(sessionKey);
  try {
    if (sessionStorage.getItem(markerKey) !== '1') return false;
    sessionStorage.removeItem(markerKey);
    return true;
  } catch {
    return false;
  }
}

function isInvalidTabTitle(title: string | undefined): boolean {
  const value = title?.trim();
  return !value || value === 'undefined' || value.startsWith('nav.undefined');
}

function fallbackTabTitleForPath(path: string): string {
  const pathname = path.split('?')[0] ?? path;
  if (pathname === '/' || pathname === '') return 'Home';
  if (pathname === '/driver/trips') return 'My Trips';
  if (pathname === '/driver/salary') return 'My Pay';
  if (pathname === '/pwa/tm/tasks') return 'Mobile Tasks';
  if (pathname === '/pwa/ct/tasks') return 'Mobile Tasks';
  if (pathname === '/pwa/wm/tasks') return 'Mobile Tasks';
  if (pathname === '/pwa/cf/tasks') return 'Mobile Tasks';
  if (pathname === '/pwa/fm/tasks') return 'Mobile Tasks';
  if (pathname === '/pwa/ac/tasks' || pathname === '/pwa/acc/tasks') return 'Mobile Tasks';
  const last = pathname.split('/').filter(Boolean).at(-1);
  return last ? last.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) : 'Workspace';
}

function sanitizeTab(tab: MdiTab): MdiTab {
  if (!isInvalidTabTitle(tab.title)) return tab;
  return { ...tab, title: fallbackTabTitleForPath(tab.path) };
}

function normalizeLegacyTabPath(path: string): string {
  const [pathname, query = ''] = path.split('?');
  const mode = new URLSearchParams(query).get('mode');
  if (mode === 'RETURN' && (pathname === '/wp/inbound/new' || pathname === '/wm/inbound/new')) {
    return pathname.startsWith('/wm') ? '/wm/returns/form' : '/wp/returns/form';
  }
  return path
    .replace(/^\/crm\b/, '/rm')
    .replace(/^\/tms\b/, '/tm')
    .replace(/^\/wms\b/, '/wm')
    .replace(/^\/hms\b/, '/ct')
    .replace(/^\/fms\b/, '/fm')
    .replace(/^\/scm\b/, '/sc')
    .replace(/^\/acc\b/, '/ac')
    .replace(/^\/aiw\b/, '/ai')
    .replace(/^\/aic\b/, '/ai')
    .replace(/^\/maritime-desk\b/, '/mt')
    .replace(/^\/shipping-agency\b/, '/mt')
    .replace(/^\/liner\b/, '/mt');
}

function pathnameForTabPath(path: string): string {
  return normalizeLegacyTabPath(path.split('?')[0] ?? path);
}

function tabIdentityForPath(path: string): string {
  const normalized = normalizeLegacyTabPath(path);
  return pathnameForTabPath(normalized);
}

// Restore from sessionStorage for page refresh persistence
function loadSession(sessionKey: string | null): { tabs: MdiTab[]; activeTabId: string | null } {
  if (!sessionKey) {
    return { tabs: [], activeTabId: null };
  }
  try {
    const raw = sessionStorage.getItem(sessionKey);
    if (raw) {
      const data = JSON.parse(raw);
      if (data.tabs?.length) {
        const migratedTabs = migrateTabs(data.tabs as MdiTab[]).map(sanitizeTab);
        const activeTabId = migratedTabs.some((tab) => tab.id === data.activeTabId)
          ? data.activeTabId
          : (migratedTabs[0]?.id ?? null);
        return { tabs: migratedTabs, activeTabId };
      }
    }
  } catch { /* ignore */ }
  return { tabs: [], activeTabId: null };
}

function saveSession(tabs: MdiTab[], activeTabId: string | null) {
  if (!currentSessionKey) return;
  try {
    sessionStorage.setItem(currentSessionKey, JSON.stringify({ tabs, activeTabId }));
  } catch { /* ignore */ }
}

function clearLegacySession() {
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch { /* ignore */ }
}

const initial = { tabs: [] as MdiTab[], activeTabId: null as string | null };

export const useMdiStore = create<MdiState>((set, get) => ({
  tabs: initial.tabs,
  activeTabId: initial.activeTabId,
  sessionScope: null,

  openTab: (input) => {
    const { tabs, activeTabId } = get();
    const normalizedInput = { ...input, path: normalizeLegacyTabPath(input.path) };
    const safeTitle = isInvalidTabTitle(normalizedInput.title) ? fallbackTabTitleForPath(normalizedInput.path) : normalizedInput.title.replace(/\bCRM\b/g, 'RM');
    // Match by logical page identity. Most lists de-dupe by pathname, while
    // mode-driven workbenches keep distinct tabs because they render different flows.
    const inputIdentity = tabIdentityForPath(normalizedInput.path);
    const existing = tabs.find((t) => tabIdentityForPath(t.path) === inputIdentity);
    if (existing) {
      const nextClosable = normalizedInput.closable ?? existing.closable;
      const existingTitle = isInvalidTabTitle(existing.title) ? fallbackTabTitleForPath(existing.path) : existing.title;
      const needsTabUpdate = existingTitle !== safeTitle
        || existing.path !== normalizedInput.path
        || existing.icon !== normalizedInput.icon
        || existing.closable !== nextClosable;
      const needsActivation = activeTabId !== existing.id;

      if (!needsTabUpdate && !needsActivation) {
        return existing.id;
      }

      const newTabs = needsTabUpdate
        ? tabs.map((tab) => (
            tab.id === existing.id
              ? {
                  ...tab,
                  title: safeTitle,
                  path: normalizedInput.path,
                  icon: normalizedInput.icon,
                  closable: nextClosable,
                }
              : tab
          ))
        : tabs;

      set({ tabs: newTabs, activeTabId: existing.id });
      saveSession(newTabs, existing.id);
      return existing.id;
    }
    // Create new tab and keep the full path, including query string.
    // De-duplication still happens by pathname above.
    const id = `tab-${nextTabId++}`;
    const newTab: MdiTab = {
      id,
      title: safeTitle,
      path: normalizedInput.path,
      icon: normalizedInput.icon,
      closable: normalizedInput.closable ?? true,
    };
    const newTabs = [...tabs, newTab];
    set({ tabs: newTabs, activeTabId: id });
    saveSession(newTabs, id);
    return id;
  },

  ensureTab: (input) => {
    const { tabs, activeTabId } = get();
    const normalizedInput = { ...input, path: normalizeLegacyTabPath(input.path) };
    const safeTitle = isInvalidTabTitle(normalizedInput.title) ? fallbackTabTitleForPath(normalizedInput.path) : normalizedInput.title.replace(/\bCRM\b/g, 'RM');
    const inputIdentity = tabIdentityForPath(normalizedInput.path);
    const existing = tabs.find((tab) => tabIdentityForPath(tab.path) === inputIdentity);

    if (existing) {
      const nextClosable = normalizedInput.closable ?? existing.closable;
      const existingTitle = isInvalidTabTitle(existing.title) ? fallbackTabTitleForPath(existing.path) : existing.title;
      const needsTabUpdate = existingTitle !== safeTitle
        || existing.path !== normalizedInput.path
        || existing.icon !== normalizedInput.icon
        || existing.closable !== nextClosable;
      if (!needsTabUpdate) return existing.id;

      const newTabs = tabs.map((tab) => (
        tab.id === existing.id
          ? {
              ...tab,
              title: safeTitle,
              path: normalizedInput.path,
              icon: normalizedInput.icon,
              closable: nextClosable,
            }
          : tab
      ));
      set({ tabs: newTabs });
      saveSession(newTabs, activeTabId);
      return existing.id;
    }

    const id = `tab-${nextTabId++}`;
    const newTab: MdiTab = {
      id,
      title: safeTitle,
      path: normalizedInput.path,
      icon: normalizedInput.icon,
      closable: normalizedInput.closable ?? true,
    };
    const newTabs = [...tabs, newTab];
    set({ tabs: newTabs });
    saveSession(newTabs, activeTabId);
    return id;
  },

  closeTab: (tabId) => {
    const { tabs, activeTabId } = get();
    const idx = tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return activeTabId;

    const newTabs = tabs.filter((t) => t.id !== tabId);
    let nextActive: string | null = null;

    if (newTabs.length === 0) {
      nextActive = null;
    } else if (activeTabId === tabId) {
      // Activate adjacent tab
      nextActive = newTabs[Math.min(idx, newTabs.length - 1)]!.id;
    } else {
      nextActive = activeTabId;
    }

    set({ tabs: newTabs, activeTabId: nextActive });
    saveSession(newTabs, nextActive);
    return nextActive ? (newTabs.find((t) => t.id === nextActive)?.path ?? null) : null;
  },

  activateTab: (tabId) => {
    const { tabs } = get();
    if (tabs.some((t) => t.id === tabId)) {
      set({ activeTabId: tabId });
      saveSession(tabs, tabId);
    }
  },

  updateTabTitle: (tabId, title) => {
    const { tabs, activeTabId } = get();
    const newTabs = tabs.map((t) => (t.id === tabId ? { ...t, title } : t));
    set({ tabs: newTabs });
    saveSession(newTabs, activeTabId);
  },

  closeAllTabs: () => {
    const { tabs } = get();
    // Keep only non-closable tabs (the dashboard tab has closable: false)
    const kept = tabs.filter((t) => !t.closable);
    const firstKept = kept[0] ?? tabs[0]; // guaranteed to keep at least one
    const newTabs = firstKept ? (kept.length > 0 ? kept : [tabs[0]!]) : [];
    const nextActive = newTabs[0]?.id ?? null;
    set({ tabs: newTabs, activeTabId: nextActive });
    saveSession(newTabs, nextActive);
    return newTabs[0]?.path ?? null;
  },

  closeTabsToDashboard: ({ dashboardPath, dashboardTitle, dashboardIcon, scope }) => {
    const { tabs } = get();
    const dashboardPathname = dashboardPath.split('?')[0] ?? dashboardPath;
    const isInScope = scope ?? (() => true);
    const existingDashboard = tabs.find((tab) => (
      (tab.path.split('?')[0] ?? tab.path) === dashboardPathname
    ));
    const dashboardTab: MdiTab = existingDashboard
      ? {
          ...existingDashboard,
          title: dashboardTitle,
          path: dashboardPath,
          icon: dashboardIcon ?? existingDashboard.icon,
          closable: false,
        }
      : {
          id: `tab-${nextTabId++}`,
          title: dashboardTitle,
          path: dashboardPath,
          icon: dashboardIcon,
          closable: false,
        };

    const globalPinnedTabs = tabs.filter((tab) => (
      tab.id !== dashboardTab.id
      && !tab.closable
      && !isInScope(tab)
    ));
    const outsideTabs = tabs.filter((tab) => {
      if (tab.id === dashboardTab.id) return false;
      if (globalPinnedTabs.some((pinned) => pinned.id === tab.id)) return false;
      return !isInScope(tab);
    });
    const nextTabs = [...globalPinnedTabs, dashboardTab, ...outsideTabs];

    set({ tabs: nextTabs, activeTabId: dashboardTab.id });
    saveSession(nextTabs, dashboardTab.id);
    return dashboardTab.path;
  },

  syncSessionScope: ({ tenantId, userId }) => {
    const nextSessionKey = buildSessionKey({ tenantId, userId });
    if (nextSessionKey === currentSessionKey) return;

    clearLegacySession();
    currentSessionKey = nextSessionKey;

    if (!currentSessionKey) {
      computeNextTabId([]);
      set({ tabs: [], activeTabId: null, sessionScope: null });
      return;
    }

    if (consumeFreshSessionMarker(currentSessionKey)) {
      try {
        sessionStorage.removeItem(currentSessionKey);
      } catch { /* ignore */ }
      computeNextTabId([]);
      set({
        tabs: [],
        activeTabId: null,
        sessionScope: currentSessionKey,
      });
      return;
    }

    const restored = loadSession(currentSessionKey);
    computeNextTabId(restored.tabs);
    set({
      tabs: restored.tabs,
      activeTabId: restored.activeTabId,
      sessionScope: currentSessionKey,
    });
  },

  resetTabs: () => {
    if (currentSessionKey) {
      try {
        sessionStorage.removeItem(currentSessionKey);
      } catch { /* ignore */ }
    }
    computeNextTabId([]);
    set({ tabs: [], activeTabId: null });
  },
}));
